// "POs not sent yet" — the outlet-wide card on the Supply home and order pages
// (Shaq, 2026-09-17: every unsent PO for the outlet, not just the raiser's,
// with a nudge when one looks forgotten). Behind
// supply_po_edit_before_send_enabled, like editing.
//
// Directive-free on purpose: the loader (order/status-rail-data.ts) and the
// client card (_components/UnsentPoCard.tsx) both read these rules, and a
// plain function exported from a "use client" module cannot be called by a
// server component (the 2026-09-16 GRN-page outage).

import { todayIsoMyt } from "@/lib/time";

import { fillCopy, pick, type Copy, type SupplyLang } from "./lang";

/** One row of the outlet-wide "POs not sent yet" card — serialisable. */
export type UnsentPoRow = {
  orderId: string;
  docCode: string;
  supplierName: string;
  /** null = at least one line has no price: the card says "Price needed". */
  totalLabel: string | null;
  raisedByLabel: string;
  /** "hari ini" / "semalam" / "3 hari lalu"; null when approved_at is unreadable. */
  ageLabel: string | null;
  /** Approved over 72h ago, or past its needed-by day (MYT): ask "Still needed?". */
  stale: boolean;
  /** WhatsApp supplier: the existing share → "I sent it" path records the send. */
  whatsapp: boolean;
  /**
   * This viewer may send / record it (canAccessSupplyPo: an approver, or the
   * person who raised it). Otherwise a WhatsApp row shows `recordAskLabel`.
   */
  canRecordSend: boolean;
  /** "Ask {raiser} or an approver to send and record this PO." */
  recordAskLabel: string;
  /** No active WhatsApp or email channel at all: HQ must add one before anyone can send. */
  noChannel: boolean;
  /** "Edit before sending" door, or null when this viewer may not edit here. */
  editHref: string | null;
  /** This viewer may cancel it (the same rule as editing: canEditSupplyPoAtOutlet). */
  canCancel: boolean;
};

export type UnsentPoCardModel =
  | {
      state: "ready";
      /** Oldest approval first. */
      rows: UnsentPoRow[];
      /** Approved POs whose delivery already STARTED (an open GRN): never "to send". */
      started: { count: number; href: string } | null;
    }
  /** The read failed — the card says so; it never renders as "nothing to send". */
  | { state: "failed" };

/** A PO approved longer ago than this, and still not sent, gets the nudge. */
export const UNSENT_PO_STALE_AFTER_MS = 72 * 60 * 60 * 1000;

/** Rows shown before "Show all" — the oldest ones, which most need a decision. */
export const UNSENT_PO_VISIBLE_ROWS = 3;

const DAY_MS = 24 * 60 * 60 * 1000;

function isoDayNumber(isoDay: string): number {
  const [year, month, day] = isoDay.split("-").map(Number);
  return Date.UTC(year!, month! - 1, day!) / DAY_MS;
}

/**
 * Whole MALAYSIAN calendar days from `fromIso` to `now` — "approved yesterday"
 * means the day before today in MYT, not 24 hours ago. Built on todayIsoMyt
 * (+8h, then the UTC date), so the device/server zone never matters. Null
 * for a missing or unreadable timestamp; never negative.
 */
export function mytDaysSince(fromIso: string | null | undefined, now: Date): number | null {
  if (!fromIso) return null;
  const from = new Date(fromIso);
  if (Number.isNaN(from.getTime())) return null;
  return Math.max(0, isoDayNumber(todayIsoMyt(now)) - isoDayNumber(todayIsoMyt(from)));
}

/**
 * Should this unsent PO ask "Still needed?"
 *
 *  - approved MORE than 72 hours ago, or
 *  - its needed-by day (a MYT calendar date) is before today in MYT.
 *
 * The caller only asks about POs with no GRN: a delivery that started is a
 * different job (finish receiving), never "still needed?".
 */
export function isStaleUnsentPo(
  order: { approved_at?: string | null; needed_by?: string | null },
  now: Date,
): boolean {
  if (order.approved_at) {
    const approved = new Date(order.approved_at).getTime();
    if (!Number.isNaN(approved) && now.getTime() - approved > UNSENT_PO_STALE_AFTER_MS) return true;
  }
  return Boolean(order.needed_by) && order.needed_by! < todayIsoMyt(now);
}

export const UNSENT_PO_COPY = {
  heading: { ms: "PO belum dihantar", en: "POs not sent yet" },
  priceNeeded: { ms: "Perlu harga", en: "Price needed" },
  raisedBy: { ms: "Dibuat oleh {name}", en: "Raised by {name}" },
  unknownRaiser: { ms: "tak diketahui", en: "unknown" },
  today: { ms: "hari ini", en: "today" },
  yesterday: { ms: "semalam", en: "yesterday" },
  daysAgo: { ms: "{n} hari lalu", en: "{n} days ago" },
  stillNeeded: { ms: "Masih perlu?", en: "Still needed?" },
  staleHint: {
    ms: "PO ni dah lama belum dihantar. Kalau dah hantar, rekodkan. Kalau tak perlu lagi, batalkan.",
    en: "This PO has not been sent for a while. If you sent it, record it. If it is no longer needed, cancel it.",
  },
  alreadySent: { ms: "Dah hantar", en: "Already sent it" },
  send: { ms: "Hantar PO", en: "Send PO" },
  hideSend: { ms: "Tutup", en: "Close" },
  recordAsk: {
    ms: "Minta {name} atau pelulus hantar PO ni dan rekodkan.",
    en: "Ask {name} or an approver to send this PO and record it.",
  },
  recordAskNoRaiser: {
    ms: "Minta pelulus hantar PO ni dan rekodkan.",
    en: "Ask an approver to send this PO and record it.",
  },
  hqSends: {
    ms: "Pembekal ni bukan di WhatsApp — HQ yang hantar PO ni.",
    en: "This supplier is not on WhatsApp — HQ sends this PO.",
  },
  cancel: { ms: "Batal PO", en: "Cancel PO" },
  cancelReasonLabel: { ms: "Kenapa batal?", en: "Why cancel it?" },
  cancelReasonPlaceholder: { ms: "Contoh: dah tak perlu", en: "e.g. no longer needed" },
  cancelConfirm: { ms: "Ya, batal PO", en: "Yes, cancel PO" },
  cancelKeep: { ms: "Tak jadi", en: "Keep it" },
  cancelling: { ms: "Membatalkan…", en: "Cancelling…" },
  showAll: { ms: "Tunjuk semua ({n})", en: "Show all ({n})" },
  showFewer: { ms: "Tunjuk sikit", en: "Show fewer" },
  startedOne: { ms: "1 penghantaran dah mula tapi belum siap", en: "1 delivery started but not finished" },
  startedMany: { ms: "{n} penghantaran dah mula tapi belum siap", en: "{n} deliveries started but not finished" },
  finishReceiving: { ms: "Siapkan terima barang", en: "Finish receiving" },
  loadFailed: {
    ms: "Senarai PO belum dihantar tak dapat dibaca — segar semula.",
    en: "Could not load the POs not sent yet — refresh.",
  },
} as const satisfies Record<string, Copy>;

/** "hari ini" / "semalam" / "3 hari lalu" — null when the age is unknown. */
export function unsentPoAgeLabel(days: number | null, lang: SupplyLang): string | null {
  if (days === null) return null;
  if (days === 0) return pick(lang, UNSENT_PO_COPY.today);
  if (days === 1) return pick(lang, UNSENT_PO_COPY.yesterday);
  return fillCopy(pick(lang, UNSENT_PO_COPY.daysAgo), { n: days });
}

/** Who may send / record a PO this viewer may not (the raiser, or an approver). */
export function unsentPoRecordAskLabel(raiserName: string | null, lang: SupplyLang): string {
  return raiserName
    ? fillCopy(pick(lang, UNSENT_PO_COPY.recordAsk), { name: raiserName })
    : pick(lang, UNSENT_PO_COPY.recordAskNoRaiser);
}

export function unsentPoRaisedByLabel(name: string | null, lang: SupplyLang): string {
  return fillCopy(pick(lang, UNSENT_PO_COPY.raisedBy), { name: name ?? pick(lang, UNSENT_PO_COPY.unknownRaiser) });
}

export function unsentPoShowAllLabel(total: number, lang: SupplyLang): string {
  return fillCopy(pick(lang, UNSENT_PO_COPY.showAll), { n: total });
}

export function startedDeliveriesLabel(count: number, lang: SupplyLang): string {
  return count === 1
    ? pick(lang, UNSENT_PO_COPY.startedOne)
    : fillCopy(pick(lang, UNSENT_PO_COPY.startedMany), { n: count });
}

/** Oldest approval first (a missing stamp sorts last), then id — a stable order. */
export function compareOldestApproved(
  a: { id: string; approved_at?: string | null },
  b: { id: string; approved_at?: string | null },
): number {
  const at = approvedTime(a.approved_at);
  const bt = approvedTime(b.approved_at);
  if (at !== bt) return at < bt ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

function approvedTime(iso: string | null | undefined): number {
  const time = iso ? new Date(iso).getTime() : Number.NaN;
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
}
